import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentLabel, api, getErrorMessage } from '../api/client';
import type { McpConnection } from '../api/types';

/** How to connect an MCP client, and the agents connected as the signed-in user. */
export function Agents() {
  const qc = useQueryClient();
  const tokens = useQuery({
    queryKey: ['mcp-tokens'],
    queryFn: () => api<{ tokens: McpConnection[] }>('/api/oauth/tokens'),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api(`/api/oauth/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['mcp-tokens'] }),
  });
  const mcpUrl = `${window.location.origin}/mcp`;

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-navy">Agents</h1>

      <section className="mb-6 rounded border border-gray-200 bg-white p-4 text-sm">
        <h2 className="mb-2 font-semibold text-navy">Connect an MCP client</h2>
        <p className="text-gray-700">
          Add FABS to Claude, Cursor, or any other MCP client as a remote server at this URL. The client opens
          Microsoft sign-in here, and you allow it before it gets the same access you have.
        </p>
        <code className="mt-2 block break-all rounded bg-gray-50 p-2">{mcpUrl}</code>
        <p className="mt-3 text-gray-700">Claude Code:</p>
        <code className="mt-1 block break-all rounded bg-gray-50 p-2">claude mcp add --transport http fabs {mcpUrl}</code>
      </section>

      <h2 className="mb-2 font-semibold text-navy">Connected agents</h2>
      {disconnect.isError ? <p className="mb-3 text-sm text-red-600">{getErrorMessage(disconnect.error)}</p> : null}
      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="p-2">Agent</th>
              <th className="p-2">Connected</th>
              <th className="p-2">Last used</th>
              <th className="p-2">Expires</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {tokens.data?.tokens.map((t) => (
              <tr key={t.id} className="border-b">
                <td className="p-2">{agentLabel(t)}</td>
                <td className="p-2 text-xs text-gray-500">{new Date(t.createdUtc).toLocaleString()}</td>
                <td className="p-2 text-xs text-gray-500">
                  {t.lastUsedUtc ? new Date(t.lastUsedUtc).toLocaleString() : '—'}
                </td>
                <td className="p-2 text-xs text-gray-500">{new Date(t.expiresUtc).toLocaleDateString()}</td>
                <td className="p-2 text-right">
                  <button
                    className="text-xs text-red-600 hover:underline disabled:opacity-50"
                    disabled={disconnect.isPending}
                    onClick={() => disconnect.mutate(t.id)}
                  >
                    Disconnect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tokens.data?.tokens.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">No agents are connected as you.</div>
        ) : null}
        {tokens.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {tokens.isError ? <div className="p-3 text-sm text-red-600">{getErrorMessage(tokens.error)}</div> : null}
      </div>
    </div>
  );
}
